export type VirtualKeyboardData = {
    fullHeight?: number;
    visibleHeight: number;
    keyboardHeight?: number;
};

/**
 * It is different for each device / virtual keyboard.
 * 300ms is usually the upper limit for most devices.
 * There is no official source on this, I have asked ChatGPT.
 */
export const VKEYBOARD_ANIMATION_DURATION = 300;

/**
 * Class that detects virtual keyboard appearance on mobile devices and dispatches events with viewport measurements.
 * It works by listening to resize, scroll, and focus events to determine when the keyboard is likely to be open or closed.
 * There is no direct API to detect the keyboard, so this is a workaround.
 */
export class VirtualKeyboardDetector {
    private initialHeight: number;
    private currentVisibleHeight: number;
    private isMobile: boolean;
    private isListening: boolean;
    private listeners: Map<string, (data: VirtualKeyboardData) => void>;
    private nextListenerId: number = 1;

    // Bound handlers to avoid creating new function references
    private boundUpdateHandler: () => void;
    private boundOrientationChangeHandler: () => void;
    private boundFocusInHandler: (e: FocusEvent) => void;
    private boundFocusOutHandler: (e: FocusEvent) => void;

    constructor(isMobile: boolean) {
        this.isMobile = isMobile;
        this.initialHeight = window.innerHeight;
        this.currentVisibleHeight = window.innerHeight;
        this.isListening = false;
        this.listeners = new Map();

        this.boundUpdateHandler = this.handleViewportUpdate.bind(this);
        this.boundOrientationChangeHandler = this.handleOrientationChange.bind(this);
        this.boundFocusInHandler = this.handleFocusIn.bind(this);
        this.boundFocusOutHandler = this.handleFocusOut.bind(this);
    }

    /**
     * Create a listener for keyboard changes
     *
     * @param callback Function to call with keyboard data when changes occur
     * @returns Listener ID string that can be used to remove the listener
     */
    public createListener(callback: (data: VirtualKeyboardData) => void): string {
        if (!this.isMobile) {
            return '';
        }

        const listenerId = `keyboard-listener-${this.nextListenerId++}`;

        // Add to listeners map
        this.listeners.set(listenerId, callback);

        // Start listening if this is the first listener
        if (this.listeners.size === 1) {
            this.startListening();
        }

        // Send initial data to new listener
        callback(this.calculateData());

        return listenerId;
    }

    /**
     * Remove a listener by its ID
     *
     * @param listenerId The ID returned from createListener
     * @returns boolean indicating if the listener was found and removed
     */
    public removeListener(listenerId: string): boolean {
        const removed = this.listeners.delete(listenerId);

        // Stop listening if no more listeners
        if (removed && this.listeners.size === 0) {
            this.stopListening();
        }

        return removed;
    }

    /**
     * Start listening to events that may indicate keyboard appearance
     */
    private startListening(): void {
        if (!this.isMobile || this.isListening) return;

        this.isListening = true;

        // Initialize heights
        this.initialHeight = window.innerHeight;
        this.currentVisibleHeight = window.innerHeight;

        // Set up the viewport change listeners
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this.boundUpdateHandler);
            window.visualViewport.addEventListener('scroll', this.boundUpdateHandler);
        } else {
            // Fallback for browsers without visualViewport API
            window.addEventListener('resize', this.boundUpdateHandler);
        }

        // Handle orientation change
        window.addEventListener('orientationchange', this.boundOrientationChangeHandler);

        // Listen for focus events to detect when keyboard might open
        document.addEventListener('focusin', this.boundFocusInHandler);

        // Listen for blur events to detect when keyboard might close
        document.addEventListener('focusout', this.boundFocusOutHandler);
    }

    /**
     * Stop listening to events to save some CPU work
     */
    private stopListening(): void {
        if (!this.isListening) return;

        this.isListening = false;

        // Remove all event listeners
        if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', this.boundUpdateHandler);
            window.visualViewport.removeEventListener('scroll', this.boundUpdateHandler);
        } else {
            window.removeEventListener('resize', this.boundUpdateHandler);
        }

        window.removeEventListener('orientationchange', this.boundOrientationChangeHandler);
        document.removeEventListener('focusin', this.boundFocusInHandler);
        document.removeEventListener('focusout', this.boundFocusOutHandler);
    }

    /**
     * Handle orientation change events
     */
    private handleOrientationChange(): void {
        // Reset initial height after orientation change
        setTimeout(() => {
            if (
                !document.activeElement ||
                (document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA')
            ) {
                this.initialHeight = window.innerHeight;
                this.currentVisibleHeight = window.innerHeight;
            } else {
                // If keyboard is likely open, just update visible height
                if (window.visualViewport) {
                    this.currentVisibleHeight = window.visualViewport.height;
                } else {
                    this.currentVisibleHeight = window.innerHeight;
                }
            }
            this.notifyListeners();
        }, VKEYBOARD_ANIMATION_DURATION);
    }

    /**
     * Handle focus events to detect when keyboard might open
     */
    private handleFocusIn(e: FocusEvent): void {
        if (e.target instanceof HTMLElement) {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                // Small delay to let the keyboard open
                setTimeout(() => {
                    if (window.visualViewport) {
                        this.currentVisibleHeight = window.visualViewport.height;
                    } else {
                        this.currentVisibleHeight = window.innerHeight;
                    }
                    this.notifyListeners();
                }, VKEYBOARD_ANIMATION_DURATION);
            }
        }
    }

    /**
     * Handle blur events to detect when keyboard might close
     */
    private handleFocusOut(e: FocusEvent): void {
        if (e.target instanceof HTMLElement) {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                // Small delay to let the keyboard close
                setTimeout(() => {
                    if (window.visualViewport) {
                        this.currentVisibleHeight = window.visualViewport.height;
                    } else {
                        this.currentVisibleHeight = window.innerHeight;
                    }
                    this.notifyListeners();
                }, VKEYBOARD_ANIMATION_DURATION);
            }
        }
    }

    /**
     * Handle viewport updates from visualViewport or window resize events
     */
    private handleViewportUpdate(): void {
        if (window.visualViewport) {
            this.currentVisibleHeight = window.visualViewport.height;
        } else {
            this.currentVisibleHeight = window.innerHeight;
        }
        this.notifyListeners();
    }

    /**
     * Notify all listeners with the current data
     */
    private notifyListeners(): void {
        if (this.listeners.size === 0) return;

        const data = this.calculateData();

        // Call each listener with the data
        this.listeners.forEach((callback) => {
            try {
                callback(data);
            } catch (err) {
                console.error('Error in virtual keyboard listener:', err);
            }
        });
    }

    /**
     * Calculate current measurements data
     */
    private calculateData(): VirtualKeyboardData {
        const fullHeight = this.initialHeight;
        const visibleHeight = this.currentVisibleHeight;
        const keyboardHeight = Math.max(0, fullHeight - visibleHeight);

        return {
            fullHeight,
            visibleHeight,
            keyboardHeight,
        };
    }
}

let instance: VirtualKeyboardDetector | null = null;

const getInstance = (isMobile?: boolean): VirtualKeyboardDetector => {
    if (!instance) {
        instance = new VirtualKeyboardDetector(isMobile);
    }

    return instance;
};

export interface IVirtualKeyboardDetector {
    createListener: (callback: (data: VirtualKeyboardData) => void, isMobile: boolean) => string;
    removeListener: (listenerId: string) => boolean;
}

export const virtualKeyboardDetector: IVirtualKeyboardDetector = {
    createListener: (callback: (data: VirtualKeyboardData) => void, isMobile: boolean) =>
        getInstance(isMobile).createListener(callback),
    removeListener: (listenerId: string) => getInstance().removeListener(listenerId),
};
